import React, { useEffect, useMemo, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useLocale } from '../contexts/LocaleContext';
import { useCreateEntityFromMentionOptional } from '../contexts/CreateEntityFromMentionContext';
import { ENTITY_TYPE_LABEL_KEYS } from '../utils/entity-utils';
import { listKitExtension } from '../utils/editor-extensions';
import {
  createEntityMentionExtension,
  MENTION_ADD_NEW_LABEL_KEYS,
} from '../utils/entity-mention-extension';
import { mergeMentionableEntities } from '../utils/mention-suggestions';
import { FormattingBar } from './FormattingBar';
import { isDialogPrimaryShortcutKey } from './ui/dialog-primary-shortcut';

export type CreateableMentionableEntityType =
  | 'character'
  | 'location'
  | 'manual_chapter'
  | 'storyline'
  | 'scene'
  | 'resource'
  | 'timeline_item'
  | 'document';

export type MentionableEntityType = CreateableMentionableEntityType | 'user';

export interface MentionableEntity {
  id: string;
  label: string;
  type: MentionableEntityType;
  documentId?: string;
  documentTitle?: string;
  order?: number;
}

export interface RichTextFieldProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: string;
  maxHeight?: string;
  className?: string;
  id?: string;
  mentionableEntities?: MentionableEntity[];
  editable?: boolean;
  autoFocus?: boolean;
  focusToken?: number;
  onSubmit?: () => void;
  onCancel?: () => void;
  submitHint?: string;
}

export const RichTextField: React.FC<RichTextFieldProps> = ({
  value,
  onChange,
  placeholder,
  minHeight = '120px',
  maxHeight,
  className = '',
  id,
  mentionableEntities = [],
  editable = true,
  autoFocus = false,
  focusToken,
  onSubmit,
  onCancel,
  submitHint,
}) => {
  const { t } = useLocale();
  const createEntityFromMention = useCreateEntityFromMentionOptional();
  const createdEntities = createEntityFromMention?.createdEntities ?? [];
  const allMentionableEntities = useMemo(
    () => mergeMentionableEntities(mentionableEntities, createdEntities),
    [mentionableEntities, createdEntities],
  );
  const mentionableEntitiesRef = useRef(allMentionableEntities);
  mentionableEntitiesRef.current = allMentionableEntities;
  const createRequest = createEntityFromMention?.request ?? null;
  const suppressMentionSuggestionsRef = useRef(false);
  suppressMentionSuggestionsRef.current = createRequest != null;
  const openCreateEntityRef = useRef(createEntityFromMention?.openCreateEntity);
  openCreateEntityRef.current = createEntityFromMention?.openCreateEntity;
  const getCreateableTypesRef = useRef(createEntityFromMention?.getCreateableTypes);
  getCreateableTypesRef.current = createEntityFromMention?.getCreateableTypes;
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const mentionHint = useMemo(() => {
    if (allMentionableEntities.length === 0) return null;
    const types = [...new Set(allMentionableEntities.map((e) => e.type))]
      .sort()
      .map((type) =>
        type === 'user'
          ? t('shared_with')
          : t(ENTITY_TYPE_LABEL_KEYS[type as keyof typeof ENTITY_TYPE_LABEL_KEYS]),
      )
      .join(', ');
    return `${t('rich_text_mention_hint')}${types}`;
  }, [allMentionableEntities, t]);

  const editorStyle = [
    `min-height: ${minHeight}`,
    maxHeight ? `max-height: ${maxHeight}` : '',
    maxHeight ? 'overflow-y: auto' : '',
  ].filter(Boolean).join('; ');

  const mentionExtension = useMemo(() => {
    return createEntityMentionExtension({
      getMentionableEntities: () => mentionableEntitiesRef.current,
      getTypeLabel: (type) =>
        type === 'user'
          ? t('shared_with')
          : t(ENTITY_TYPE_LABEL_KEYS[type as keyof typeof ENTITY_TYPE_LABEL_KEYS] ?? 'Documents'),
      getNoMatchLabel: () => t('mention_no_matching_entry'),
      getCreateLabel: (type) => t(MENTION_ADD_NEW_LABEL_KEYS[type]),
      getCreateableTypes: (fallbackDocumentId) => getCreateableTypesRef.current?.(fallbackDocumentId) ?? [],
      openCreateEntity: (type, name, options) => openCreateEntityRef.current?.(type, name, options),
      shouldSuppressSuggestions: () => suppressMentionSuggestionsRef.current,
    });
  }, [t]);

  const extensions = useMemo(() => {
    const exts = [
      StarterKit.configure({
        codeBlock: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        listKeymap: false,
      }),
      listKitExtension,
    ];
    exts.push(mentionExtension as unknown as (typeof exts)[0]);
    return exts;
  }, [mentionExtension]);

  const editor = useEditor({
    extensions,
    content: value || '<p></p>',
    editable,
    editorProps: {
      attributes: {
        class: `w-full rounded-md border border-input bg-background px-3 py-2 text-base text-foreground transition-[border-color,box-shadow] duration-200 focus:outline-none focus:border-[var(--scribane-green)] focus:ring-[3px] focus:ring-[rgba(94,204,128,0.4)] [&_p]:indent-0 [&_p]:mb-0 [&_p:not(:last-child)]:mb-2 ${className}`.trim(),
        style: editorStyle,
        ...(placeholder ? { 'data-placeholder': placeholder } : {}),
      },
      handleKeyDown: (_view, event) => {
        if (onSubmitRef.current && isDialogPrimaryShortcutKey(event)) {
          event.preventDefault();
          onSubmitRef.current();
          return true;
        }
        if (event.key === 'Escape' && onCancelRef.current) {
          event.preventDefault();
          event.stopPropagation();
          onCancelRef.current();
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor }) => {
      onChangeRef.current(editor.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    if (editor.isEditable === editable) return;
    editor.setEditable(editable, false);
  }, [editor, editable]);

  useEffect(() => {
    if (!editor || editor.isDestroyed || !autoFocus || !editable) return;
    editor.commands.focus('end');
  }, [editor, autoFocus, editable]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const currentHtml = editor.getHTML();
    const normalizedValue = value || '<p></p>';
    if (normalizedValue !== currentHtml) {
      editor.commands.setContent(normalizedValue, { emitUpdate: false });
    }
  }, [editor, value]);

  useEffect(() => {
    if (!editor || editor.isDestroyed || !editable || focusToken == null || focusToken <= 0) {
      return;
    }
    editor.commands.focus('end');
  }, [editor, editable, focusToken, value]);

  if (!editor) {
    return null;
  }

  return (
    <div className="rich-text-field">
      {editable && <FormattingBar editor={editor} mode="pure" />}
      <EditorContent editor={editor} id={id} />
      {mentionHint && editable && (
        <p className="mt-1.5 text-xs text-muted-foreground">
          {mentionHint}
        </p>
      )}
      {submitHint && editable && (
        <p className="mt-1.5 text-xs text-muted-foreground">
          {submitHint}
        </p>
      )}
    </div>
  );
};
